import type { Casino } from "@/lib/types";

export const casinos: Casino[] = [
  {
    id: "casino-betinia",
    name: "Betinia.se",
    slug: "betinia",
    website_url: "https://www.betinia.se/",
    logo_url: "/brands/betinia.png",
    operator_name: "Hero Gaming Ltd (kontrollera aktuell operatör)",
    license_type: "swedish",
    license_authority: "Spelinspektionen",
    accepts_sweden: true,
    swedish_license: true,
    spelpaus_connected: true,
    bankid_supported: true,
    swish_supported: true,
    trustly_supported: true,
    zimpler_supported: true,
    brite_supported: true,
    crypto_supported: false,
    payment_methods: ["swish", "trustly", "zimpler", "brite", "visa", "mastercard"],
    game_providers: [
      "netent",
      "playngo",
      "pragmatic-play",
      "evolution",
      "yggdrasil",
      "red-tiger",
      "hacksaw-gaming",
    ],
    min_deposit: 100,
    withdrawal_speed: "Vanligtvis inom 0–24 timmar med BankID/Swish (Trustly).",
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 4.7,
    ratings: {
      safety: 4.8,
      bonus: 4.5,
      payments: 4.7,
      games: 4.6,
      mobile: 4.7,
      support: 4.5,
    },
    pros_sv: [
      "Svensk licens via Spelinspektionen",
      "Snabba uttag med BankID och Swish",
      "Brett spelutbud från ledande studios",
      "Anslutet till Spelpaus och Stödlinjen",
      "Mobilanpassat utan extra app",
    ],
    cons_sv: [
      "Endast SEK och svenska spelare på .se-versionen",
    ],
    pros_en: [
      "Licensed by Spelinspektionen in Sweden",
      "Fast withdrawals with BankID and Swish",
      "Broad game catalog from leading studios",
      "Connected to Spelpaus and Stödlinjen",
      "Mobile-first without a separate app",
    ],
    cons_en: [
      "Swedish .se version only available to Swedish residents",
    ],
    priority_brand: true,
    verification_status: "needs_review",
    source_reference: "public-info",
    published: true,
    index_status: "index",
    intro_sv:
      "Betinia.se är ett svenskt onlinecasino med svensk spellicens från Spelinspektionen. Sajten kombinerar ett brett spelutbud — från klassiska slots av Play'n GO och NetEnt till modern livecasino från Evolution — med snabba svenska betalningsmetoder som Swish, Trustly och Zimpler. Designen är pay-and-play-känsla med BankID-inloggning, vilket gör att du kan vara igång inom minuter.",
    intro_en:
      "Betinia.se is a Swedish online casino licensed by Spelinspektionen. The site combines a broad game catalog — from classic slots by Play'n GO and NetEnt to modern live casino from Evolution — with fast Swedish payment methods such as Swish, Trustly and Zimpler. The flow is pay-and-play with BankID login, meaning you can be up and running in minutes.",
    body_sv:
      "Betinia.se riktar sig uttryckligen till spelare med hemvist i Sverige. Det märks i spelutbudet, betalningsmetoderna och hela kontoflödet: BankID är obligatoriskt, insättningsgränsen ställs in vid registrering, och Spelpaus är synligt redan i sidfoten.",
    body_en:
      "Betinia.se is targeted explicitly at players resident in Sweden. It shows in the game catalog, the payment options and the whole account flow: BankID is mandatory, the deposit limit is set during registration, and Spelpaus is visible in the footer from the first click.",
    long_sections: [
      {
        heading_sv: "Licens, säkerhet och Spelpaus",
        heading_en: "License, security and Spelpaus",
        paragraphs_sv: [
          "Betinia.se opererar under svensk spellicens utfärdad av Spelinspektionen. Det innebär att casinot är anslutet till Spelpaus.se, kräver BankID-identifiering vid registrering och tillämpar svensk konsumenträtt på alla transaktioner. Insättningsgränser, förlustgränser och tidsgränser kan ändras dygnet runt direkt i kontot.",
          "All trafik mellan din enhet och casinot är krypterad. Spelutfall genereras av tredjepartscertifierade RNG-system och granskas regelbundet av Spelinspektionen. Vinster betalas i SEK och är skattefria för dig som privatperson eftersom skatten betalas av operatören.",
        ],
        paragraphs_en: [
          "Betinia.se operates under a Swedish gambling license issued by Spelinspektionen. That means the casino is connected to Spelpaus.se, requires BankID identification at registration and applies Swedish consumer law to every transaction. Deposit, loss and time limits can be changed around the clock directly in your account.",
          "All traffic between your device and the casino is encrypted. Game outcomes are produced by third-party certified RNG systems and supervised by Spelinspektionen. Winnings are paid in SEK and tax-free to you as a private individual because the tax is paid by the operator.",
        ],
      },
      {
        heading_sv: "Spelutbud och studios",
        heading_en: "Game library and studios",
        paragraphs_sv: [
          "Betinia.se har ett spelbibliotek med flera tusen titlar uppdelat på spelautomater, livecasino, jackpots och bordsspel. Du hittar moderna slots från Hacksaw Gaming, Nolimit City, Pragmatic Play och Push Gaming sida vid sida med tidlösa klassiker som Starburst och Book of Dead. Livecasino-bordsen drivs huvudsakligen av Evolution.",
          "Spelens RTP listas i varje spels infomeny — kontrollera alltid den aktuella varianten, eftersom samma slot kan finnas i flera RTP-konfigurationer hos olika operatörer.",
        ],
        paragraphs_en: [
          "Betinia.se ships a library of several thousand titles split across slots, live casino, jackpots and table games. You will find modern slots from Hacksaw Gaming, Nolimit City, Pragmatic Play and Push Gaming alongside timeless classics such as Starburst and Book of Dead. Live tables are powered primarily by Evolution.",
          "Each game's RTP is listed in its info screen — always check the current variant, as the same slot can ship in multiple RTP configurations across operators.",
        ],
      },
      {
        heading_sv: "",
        heading_en: "",
        paragraphs_sv: [
          "Vi rekommenderar att du läser igenom dem innan du klickar för att aktivera erbjudandet.",
        ],
        paragraphs_en: [
          "We recommend reading them before activating the offer.",
        ],
      },
      {
        heading_sv: "Insättningar och uttag",
        heading_en: "Deposits and withdrawals",
        paragraphs_sv: [
          "Insättningar går snabbast med Swish och Trustly — pengarna är på kontot inom sekunder. Zimpler och Brite är pay-and-play-alternativ som också använder BankID. Visa och Mastercard finns men är ovanligare på den svenska marknaden.",
          "Uttag verifieras med BankID och hamnar oftast på din bank inom 0–24 timmar via Trustly. Helger och stora helgdagar kan ge en något längre handläggningstid hos vissa banker.",
        ],
        paragraphs_en: [
          "Deposits are fastest with Swish and Trustly — funds land in seconds. Zimpler and Brite are pay-and-play options that also use BankID. Visa and Mastercard are available but rarer on the Swedish market.",
          "Withdrawals are verified with BankID and typically reach your bank within 0–24 hours via Trustly. Weekends and major public holidays can lead to slightly longer processing at some banks.",
        ],
      },
      {
        heading_sv: "Mobilupplevelse och support",
        heading_en: "Mobile experience and support",
        paragraphs_sv: [
          "Betinia.se kör i webbläsaren utan separat app — sajten är fullt mobilanpassad både på iOS och Android. Du loggar in med BankID, lobbyn är optimerad för portrait-läge, och spelen körs i HTML5 utan lagg.",
          "Kundsupport finns på svenska via livechatt och e-post. Hjälpcentret täcker de vanligaste frågorna kring konto, insättning, uttag och Spelpaus.",
        ],
        paragraphs_en: [
          "Betinia.se runs in the browser without a separate app — the site is fully mobile-optimised on both iOS and Android. You log in with BankID, the lobby is optimised for portrait mode, and games run in HTML5 without lag.",
          "Customer support is available in Swedish via live chat and email. The help centre covers the most common questions around account, deposit, withdrawal and Spelpaus.",
        ],
      },
      {
        heading_sv: "Vår sammanfattande bedömning",
        heading_en: "Our overall verdict",
        paragraphs_sv: [
          "Betinia.se är en av de starkare svenskt licensierade sajterna 2026. Spelutbudet är brett, betalningarna är snabba och regelefterlevnaden är genomgående hög. För dig som vill spela med svensk konsumenträtt, BankID och anslutning till Spelpaus är Betinia ett självklart kort att titta på.",
        ],
        paragraphs_en: [
          "Betinia.se is one of the stronger Swedish-licensed sites of 2026. The game library is broad, payments are fast and the regulatory posture is consistently strong. For players who want Swedish consumer protection, BankID and Spelpaus connectivity, Betinia is an obvious card to consider.",
        ],
      },
    ],
    updated_at: "2026-04-30",
  },
  {
    id: "casino-leovegas",
    name: "LeoVegas",
    slug: "leovegas",
    website_url: "https://www.leovegas.com/",
    logo_url: "/brands/leovegas.png",
    operator_name: "LeoVegas Gaming Plc",
    license_type: "swedish",
    license_authority: "Spelinspektionen",
    accepts_sweden: true,
    swedish_license: true,
    spelpaus_connected: true,
    bankid_supported: true,
    swish_supported: true,
    trustly_supported: true,
    zimpler_supported: true,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: ["swish", "trustly", "zimpler", "visa", "mastercard"],
    game_providers: ["netent", "microgaming", "evolution", "playngo", "pragmatic-play"],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 4.5,
    ratings: { safety: 4.8, bonus: 4.2, payments: 4.6, games: 4.5, mobile: 4.8, support: 4.4 },
    pros_sv: [
      "Erfaret bolag med svensk licens",
      "Stark mobilupplevelse",
      "Swish, Trustly och BankID stöds",
    ],
    cons_sv: [],
    pros_en: [
      "Established operator with Swedish license",
      "Strong mobile experience",
      "Swish, Trustly and BankID supported",
    ],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    published: true,
    index_status: "index",
    intro_sv:
      "LeoVegas är ett av de mest etablerade onlinecasinona på den svenska marknaden. Bolaget grundades 2011 och är noterat. Sajten har svensk spellicens och fokuserar på en stark mobilupplevelse — ”King of Mobile Casino” är deras egen positionering.",
    intro_en:
      "LeoVegas is one of the most established online casinos on the Swedish market. The company was founded in 2011 and is publicly listed. The site holds a Swedish gambling license and focuses on a strong mobile experience — ”King of Mobile Casino” is their own positioning.",
    body_sv:
      "LeoVegas erbjuder slots, livecasino och bordsspel under svensk licens.",
    body_en:
      "LeoVegas offers slots, live casino and table games under a Swedish license.",
    long_sections: [
      {
        heading_sv: "Licens och säkerhet",
        heading_en: "License and security",
        paragraphs_sv: [
          "LeoVegas opererar under svensk licens utfärdad av Spelinspektionen. Det innebär anslutning till Spelpaus.se, BankID-identifiering vid registrering, obligatoriska insättnings- och förlustgränser och svensk konsumenträtt på alla transaktioner. Bolaget är dessutom noterat på Stockholmsbörsen, vilket innebär ytterligare extern revision av räkenskaperna.",
          "All trafik är krypterad och spelutfall genereras av tredjepartscertifierade RNG-system. Vinster är skattefria för dig som privatperson eftersom 22 procent spelskatt betalas av operatören.",
        ],
        paragraphs_en: [
          "LeoVegas operates under a Swedish license issued by Spelinspektionen. That means Spelpaus.se connectivity, BankID at registration, mandatory deposit and loss limits, and Swedish consumer law on every transaction. The company is also publicly listed on Nasdaq Stockholm, which adds external audit of the books.",
          "All traffic is encrypted and game outcomes are produced by third-party certified RNG systems. Winnings are tax-free for you as a private individual because the 22 percent gambling tax is paid by the operator.",
        ],
      },
      {
        heading_sv: "Spelutbud och studios",
        heading_en: "Game library and studios",
        paragraphs_sv: [
          "LeoVegas spelutbud är ett av de bredare i Sverige med flera tusen titlar från NetEnt, Microgaming, Evolution, Play'n GO och Pragmatic Play. Live-bordsen drivs av Evolution och inkluderar både klassiska blackjack- och roulettebord och nyare game shows som Crazy Time, Monopoly Live och Lightning Roulette.",
          "Lobbyn har en bra sökfunktion och kategorisering — du hittar snabbt jackpotts, megaways och nya releaser. Spelens RTP listas i varje spels infomeny.",
        ],
        paragraphs_en: [
          "LeoVegas' library is one of the broadest in Sweden, featuring several thousand titles from NetEnt, Microgaming, Evolution, Play'n GO and Pragmatic Play. Live tables are powered by Evolution and include classic blackjack and roulette as well as newer game shows like Crazy Time, Monopoly Live and Lightning Roulette.",
          "The lobby has solid search and categorisation — jackpots, Megaways and new releases are easy to find. RTP is listed in each game's info screen.",
        ],
      },
      {
        heading_sv: "Mobilupplevelse",
        heading_en: "Mobile experience",
        paragraphs_sv: [
          "LeoVegas grundades med en mobil-först-ansats 2011 och det märks fortfarande — sajten är snabb, lobbyn är ren och flera spel är optimerade för porträttläge. Det finns en separat iOS-app i App Store. Android-användare kör webbappen direkt i Chrome.",
          "”King of Mobile Casino” är operatörens egen positionering och flera oberoende mätningar har bekräftat att tekniken hör till de starkare i Sverige.",
        ],
        paragraphs_en: [
          "LeoVegas was founded with a mobile-first approach in 2011 and it still shows — the site is fast, the lobby is clean and many games are optimised for portrait mode. There is a separate iOS app in the App Store. Android users run the web app directly in Chrome.",
          "”King of Mobile Casino” is the operator's own positioning and several independent measurements confirm the tech is among the stronger in Sweden.",
        ],
      },
      {
        heading_sv: "Insättningar och uttag",
        heading_en: "Deposits and withdrawals",
        paragraphs_sv: [
          "Insättningar går snabbt med Swish, Trustly och Zimpler. Uttag verifieras med BankID och är vanligtvis tillbaka inom 0–24 timmar via Trustly. Helger och stora helgdagar kan ge en något längre handläggningstid hos vissa banker.",
        ],
        paragraphs_en: [
          "Deposits are fast with Swish, Trustly and Zimpler. Withdrawals are verified via BankID and usually back within 0–24 hours through Trustly. Weekends and major public holidays can lead to slightly longer processing at some banks.",
        ],
      },
      {
        heading_sv: "Vår sammanfattande bedömning",
        heading_en: "Our overall verdict",
        paragraphs_sv: [
          "LeoVegas hör till de mest erfarna svensk-licensierade operatörerna och är ett tryggt val.",
        ],
        paragraphs_en: [
          "LeoVegas is one of the most experienced Swedish-licensed operators and a safe pick.",
        ],
      },
    ],
    updated_at: "2026-05-01",
  },
  {
    id: "casino-betsson",
    name: "Betsson",
    slug: "betsson",
    website_url: "https://www.betsson.com/",
    logo_url: "/brands/betsson.png",
    operator_name: "Betsson AB",
    license_type: "swedish",
    license_authority: "Spelinspektionen",
    accepts_sweden: true,
    swedish_license: true,
    spelpaus_connected: true,
    bankid_supported: true,
    swish_supported: true,
    trustly_supported: true,
    zimpler_supported: true,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: ["swish", "trustly", "zimpler", "visa", "mastercard", "skrill"],
    game_providers: ["netent", "playngo", "evolution", "pragmatic-play", "microgaming"],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 4.4,
    ratings: { safety: 4.8, bonus: 4.1, payments: 4.5, games: 4.4, mobile: 4.5, support: 4.3 },
    pros_sv: [
      "Erfaret nordiskt bolag",
      "Bra urval av betting och casino",
      "Komplett svensk licens",
    ],
    cons_sv: [],
    pros_en: [
      "Experienced Nordic operator",
      "Good mix of betting and casino",
      "Full Swedish license",
    ],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    published: true,
    index_status: "index",
    intro_sv:
      "Betsson är ett av de äldsta nordiska spelvarumärkena och driver både ett komplett onlinecasino och en sportbettingavdelning under svensk licens. Bolaget är noterat på Stockholmsbörsen och har en lång historia av nordiskt fokus.",
    intro_en:
      "Betsson is one of the oldest Nordic gambling brands, running both a full online casino and a sportsbook arm under a Swedish license. The company is listed on the Stockholm stock exchange and has a long history of Nordic focus.",
    body_sv:
      "Du hittar både slots, livecasino, poker och sportsbetting under samma konto. Det är en stor fördel för dig som vill ha allt på ett ställe.",
    body_en:
      "You can access slots, live casino, poker and sports betting under a single account. That is a big advantage for players who want everything in one place.",
    long_sections: [
      {
        heading_sv: "Bolaget bakom märket",
        heading_en: "The company behind the brand",
        paragraphs_sv: [
          "Betsson AB grundades 1963 som en spelautomatfirma och har idag vuxit till en av Nordens största speloperatörer med flera dotterbolag och varumärken. Företaget är noterat på Nasdaq Stockholm och har ett tydligt nordiskt fokus med svensk, norsk, finsk och dansk lokalisering.",
        ],
        paragraphs_en: [
          "Betsson AB was founded in 1963 as a gaming machine company and has since grown into one of the largest gambling operators in the Nordics, with several subsidiaries and brands. The company is listed on Nasdaq Stockholm and has a clear Nordic focus with Swedish, Norwegian, Finnish and Danish localisation.",
        ],
      },
      {
        heading_sv: "Casino och betting i samma konto",
        heading_en: "Casino and betting in one account",
        paragraphs_sv: [
          "En av Betssons starkaste sidor är att du kan flytta mellan casino, sportsbook och poker utan att byta konto. Det innebär en plånbok, en BankID-inloggning och en Spelpaus-anslutning. För spelare som gillar både slots och oddsbetting på fotboll eller hockey är det en praktisk fördel.",
          "Sportbettingavdelningen täcker över 30 sporter inklusive Allsvenskan, SHL och NHL. Esports-marknaderna har utökats kraftigt sedan 2024 med CS2, Dota 2 och Valorant.",
        ],
        paragraphs_en: [
          "One of Betsson's strongest features is that you can move between casino, sportsbook and poker without switching accounts. That means one wallet, one BankID login and one Spelpaus connection. For players who enjoy both slots and football or hockey betting it is a practical advantage.",
          "The sportsbook covers more than 30 sports including Allsvenskan, the SHL and the NHL. Esports markets have expanded significantly since 2024 with CS2, Dota 2 and Valorant.",
        ],
      },
      {
        heading_sv: "Spelutbud och studios",
        heading_en: "Game library and studios",
        paragraphs_sv: [
          "Casino-delen levererar spel från NetEnt, Play'n GO, Pragmatic Play, Evolution och Microgaming. Live-bordsen är från Evolution och inkluderar både svenska bord (Swedish Roulette) och game shows som Crazy Time. Antalet titlar ligger i samma härad som de andra topp-svenska sajterna.",
        ],
        paragraphs_en: [
          "The casino section ships games from NetEnt, Play'n GO, Pragmatic Play, Evolution and Microgaming. Live tables are powered by Evolution and include Swedish-language tables (Swedish Roulette) and game shows like Crazy Time. The title count sits in the same range as the other top Swedish sites.",
        ],
      },
      {
        heading_sv: "Vår sammanfattande bedömning",
        heading_en: "Our overall verdict",
        paragraphs_sv: [
          "Betsson är ett tryggt och brett alternativ — speciellt om du vill ha både casino och sportsbook hos samma operatör. Tekniken är mogen, licensen är solid och kundsupporten är på svenska.",
        ],
        paragraphs_en: [
          "Betsson is a safe, broad option — especially if you want both casino and sportsbook with the same operator. The tech is mature, the license is solid and customer support is in Swedish.",
        ],
      },
    ],
    updated_at: "2026-05-01",
  },
  {
    id: "casino-unibet",
    name: "Unibet",
    slug: "unibet",
    website_url: "https://www.unibet.se/",
    logo_url: "/brands/unibet.png",
    operator_name: "Kindred Group",
    license_type: "swedish",
    license_authority: "Spelinspektionen",
    accepts_sweden: true,
    swedish_license: true,
    spelpaus_connected: true,
    bankid_supported: true,
    swish_supported: true,
    trustly_supported: true,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: ["swish", "trustly", "visa", "mastercard"],
    game_providers: ["netent", "evolution", "playngo", "redtiger"],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 4.3,
    ratings: { safety: 4.7, bonus: 4.0, payments: 4.5, games: 4.3, mobile: 4.4, support: 4.2 },
    pros_sv: ["Stort sortiment av sport, casino och poker"],
    cons_sv: ["Färre payment-alternativ än vissa konkurrenter"],
    pros_en: ["Wide range of sports, casino and poker"],
    cons_en: ["Fewer payment options than some competitors"],
    priority_brand: false,
    verification_status: "needs_review",
    published: true,
    index_status: "index",
    intro_sv:
      "Unibet är en del av Kindred Group och driver casino, sportsbook, poker och bingo under svensk licens. Sajten är välkänd internationellt och har en stor portfölj av spel från ledande studios.",
    intro_en:
      "Unibet is part of Kindred Group and operates casino, sportsbook, poker and bingo under a Swedish license. The site is internationally well-known and ships a deep portfolio of games from leading studios.",
    body_sv:
      "Spelutbudet kombinerar moderna slots med en stark sportbettingavdelning. Live-bordsen drivs huvudsakligen av Evolution.",
    body_en:
      "The catalog combines modern slots with a strong sportsbook. Live tables are powered primarily by Evolution.",
    long_sections: [
      {
        heading_sv: "Kindred-koncernen",
        heading_en: "The Kindred group",
        paragraphs_sv: [
          "Unibet ägs av Kindred Group, en av Europas största spelkoncerner som även driver bl.a. Maria Casino, 32Red och Stan James. Kindred är noterat på Stockholmsbörsen och har sedan flera år kommunicerat ett mål om att 100 procent av intäkterna ska komma från reglerade marknader.",
        ],
        paragraphs_en: [
          "Unibet is owned by Kindred Group, one of Europe's largest gambling companies, which also runs Maria Casino, 32Red and Stan James among others. Kindred is listed on Nasdaq Stockholm and has for several years communicated a target of 100% revenue from regulated markets.",
        ],
      },
      {
        heading_sv: "Casino, betting och poker",
        heading_en: "Casino, betting and poker",
        paragraphs_sv: [
          "På Unibet kan du växla mellan casino, sportsbook, poker och bingo i samma konto. Sportsbooken är historiskt en av de starkaste i Sverige med djupa marknader på fotboll, hockey, tennis och e-sport. Pokerklienten har egna kampanjer och turneringar.",
        ],
        paragraphs_en: [
          "Unibet lets you switch between casino, sportsbook, poker and bingo in the same account. The sportsbook is historically one of the strongest in Sweden, with deep markets on football, hockey, tennis and esports. The poker client runs its own campaigns and tournaments.",
        ],
      },
      {
        heading_sv: "",
        heading_en: "",
        paragraphs_sv: [
          "Pågående kampanjer på Unibet är ofta event-baserade och knutna till stora sportevenemang.",
        ],
        paragraphs_en: [
          "Ongoing Unibet campaigns are often event-based and tied to major sporting events.",
        ],
      },
    ],
    updated_at: "2026-05-01",
  },
  {
    id: "casino-mr-vegas",
    name: "Mr Vegas",
    slug: "mr-vegas",
    website_url: "https://www.mrvegas.com/",
    logo_url: "/brands/mr-vegas.png",
    operator_name: "Videoslots Limited (Mr Vegas Affiliates)",
    license_type: "swedish",
    license_authority: "Spelinspektionen",
    accepts_sweden: true,
    swedish_license: true,
    spelpaus_connected: true,
    bankid_supported: true,
    swish_supported: true,
    trustly_supported: true,
    zimpler_supported: true,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: ["swish", "trustly", "zimpler"],
    game_providers: ["netent", "playngo", "evolution", "pragmatic-play"],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 4.2,
    ratings: { safety: 4.6, bonus: 4.0, payments: 4.4, games: 4.2, mobile: 4.3, support: 4.1 },
    pros_sv: ["Tydligt fokus på svenska spelare"],
    cons_sv: ["Kan upplevas konservativt med svensk licens"],
    pros_en: ["Clear focus on Swedish players"],
    cons_en: ["Can feel conservative under Swedish license"],
    priority_brand: false,
    verification_status: "needs_review",
    published: true,
    index_status: "index",
    intro_sv:
      "Mr Vegas är ett av Videoslots-systerbolagen med svensk spellicens.",
    intro_en:
      "Mr Vegas is one of the Videoslots sister sites holding a Swedish license.",
    body_sv:
      "Under huven är Mr Vegas en pålitlig sajt med svensk licens, BankID och Spelpaus-anslutning. Spelutbudet inkluderar både moderna slots och livecasinobord.",
    body_en:
      "Under the hood Mr Vegas is a reliable Swedish-licensed site with BankID and Spelpaus connectivity. The catalog includes modern slots and live casino tables.",
    long_sections: [
      {
        heading_sv: "Bakgrund och varumärke",
        heading_en: "Background and brand",
        paragraphs_sv: [
          "Mr Vegas är ett systerbolag till Videoslots, en av Europas största casino-operatörer som driver flera varumärken. Mr Vegas-varumärket har funnits på den svenska marknaden sedan licensreformen 2019 och positionerar sig kring snabba uttag och ett brett spelutbud.",
        ],
        paragraphs_en: [
          "Mr Vegas is a sister brand of Videoslots, one of Europe's largest casino operators which runs several brands. The Mr Vegas brand has been on the Swedish market since the 2019 license reform and is positioned around fast withdrawals and a broad game library.",
        ],
      },
      {
        heading_sv: "Spelutbud",
        heading_en: "Game library",
        paragraphs_sv: [
          "Spelbiblioteket är ett av de större på den svenska marknaden — flera tusen titlar från NetEnt, Play'n GO, Pragmatic Play, Evolution och en lång rad mindre studios. Lobbyn är funktionellt sökbar och har bra filtrering på leverantör, RTP och funktioner som Megaways.",
        ],
        paragraphs_en: [
          "The library is among the larger on the Swedish market — several thousand titles from NetEnt, Play'n GO, Pragmatic Play, Evolution and a long tail of smaller studios. The lobby has functional search with filters by provider, RTP and features such as Megaways.",
        ],
      },
      {
        heading_sv: "Betalningar och support",
        heading_en: "Payments and support",
        paragraphs_sv: [
          "Insättningar går snabbast med Swish och Trustly. Uttag verifieras med BankID och är vanligtvis tillbaka inom 0–24 timmar. Kundsupport finns på svenska via livechatt under svenska kontorstider och e-post resten av dygnet.",
        ],
        paragraphs_en: [
          "Deposits are fastest with Swish and Trustly. Withdrawals are verified with BankID and typically back within 0–24 hours. Customer support is available in Swedish via live chat during Swedish office hours and email outside those hours.",
        ],
      },
    ],
    updated_at: "2026-05-01",
  },
  {
    id: "casino-rapidi",
    name: "Rapidi",
    slug: "rapidi",
    website_url: "https://www.rapidi.com/",
    logo_url: "/brands/rapidi.svg",
    operator_name: "Internationell operatör (kontrollera aktuell licens)",
    license_type: "mga",
    license_authority: "MGA",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: true,
    swish_supported: false,
    trustly_supported: true,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: ["trustly", "visa", "mastercard"],
    game_providers: ["netent", "playngo", "pragmatic-play"],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.9,
    ratings: { safety: 3.8, bonus: 4.4, payments: 4.0, games: 4.0, mobile: 4.0, support: 3.6 },
    pros_sv: [
      "Pay-and-play upplägg",
    ],
    cons_sv: [
      "Inte anslutet till Spelpaus",
      "Mindre konsumentskydd jämfört med svensk licens",
    ],
    pros_en: ["Pay-and-play flow"],
    cons_en: ["Not connected to Spelpaus", "Less consumer protection vs Swedish license"],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Rapidi är ett internationellt onlinecasino som inte har svensk spellicens. Den är inte ansluten till Spelpaus och faller inte under svensk konsumenträtt — vilket innebär lägre skydd om något går fel.",
    intro_en:
      "Rapidi is an international online casino that does not hold a Swedish license. It is not connected to Spelpaus and is not covered by Swedish consumer law — which means lower protection if something goes wrong.",
    body_sv:
      "Innan du registrerar konto: kontrollera operatörens MGA- eller motsvarande licensnummer, läs villkoren noggrant och var medveten om att vinster från sajter utanför EES kan vara skattepliktiga.",
    body_en:
      "Before opening an account: verify the operator's MGA or equivalent license number, read the terms carefully, and remember that winnings from sites outside the EEA can be taxable.",
    long_sections: [
      {
        heading_sv: "Risker att känna till",
        heading_en: "Risks to be aware of",
        paragraphs_sv: [
          "Hjälp vid spelproblem är inte garanterat på svenska. Tvister hanteras enligt licensjurisdiktionens regler — inte svensk konsumenträtt.",
        ],
        paragraphs_en: [
          "Problem-gambling support is not guaranteed in Swedish. Disputes are resolved under the license jurisdiction's rules — not Swedish consumer law.",
        ],
      },
    ],
    updated_at: "2026-04-30",
  },
  {
    id: "casino-lyrabet",
    name: "Lyrabet",
    slug: "lyrabet",
    website_url: "https://www.lyrabet.com/",
    logo_url: "/brands/lyrabet.png",
    operator_name: "Internationell operatör",
    license_type: "curacao",
    license_authority: "Curaçao Gaming Control Board",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: true,
    payment_methods: ["bitcoin", "ethereum", "usdt", "visa", "mastercard"],
    game_providers: ["pragmatic-play", "playngo", "hacksaw-gaming", "nolimit-city"],
    support_languages: ["en", "sv"],
    mobile_supported: true,
    overall_rating: 3.4,
    ratings: { safety: 3.0, bonus: 4.2, payments: 3.6, games: 3.8, mobile: 3.5, support: 3.0 },
    pros_sv: ["Krypto-betalningar"],
    cons_sv: [
      "Curaçao-licens har lägre konsumentskydd",
      "Inte anslutet till Spelpaus",
      "Inga svenska lokala betalningar",
    ],
    pros_en: ["Crypto payments"],
    cons_en: ["Curaçao license has lower protection", "Not on Spelpaus", "No Swedish payments"],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Lyrabet är ett kryptocasino licensierat i Curaçao.",
    intro_en:
      "Lyrabet is a crypto casino licensed in Curaçao.",
    body_sv:
      "För spelare som söker krypto-betalningar är Lyrabet en av få som accepterar dessa. Sajten är dock inte ansluten till Spelpaus och saknar svensk konsumenträtt.",
    body_en:
      "For players seeking crypto payments Lyrabet is one of the few sites that accepts them. The site is not connected to Spelpaus and is not covered by Swedish consumer law.",
    long_sections: [
      {
        heading_sv: "Krypto-betalningar i praktiken",
        heading_en: "Crypto payments in practice",
        paragraphs_sv: [
          "Insättning sker genom att skicka krypto till en adress som casinot genererar. Uttag betalas tillbaka till samma typ av kryptovaluta. Transaktionerna är ofta snabbare än traditionell banköverföring men volatiliteten i kryptokursen kan påverka det faktiska värdet.",
          "Lyrabet stöder Bitcoin, Ethereum och USDT (Tether). Stablecoins som USDT är populära eftersom värdet är knutet till USD och därför inte rör sig som BTC eller ETH under den tid pengarna ligger på casinot.",
        ],
        paragraphs_en: [
          "Deposits happen by sending crypto to an address generated by the casino. Withdrawals are paid back to the same crypto. Transactions are often faster than traditional bank transfers but exchange-rate volatility can affect the realised value.",
          "Lyrabet supports Bitcoin, Ethereum and USDT (Tether). Stablecoins like USDT are popular because their value is pegged to USD, so they do not swing like BTC or ETH while sitting on the casino account.",
        ],
      },
      {
        heading_sv: "Risk och konsumentskydd",
        heading_en: "Risk and consumer protection",
        paragraphs_sv: [
          "Curaçao-licens innebär lägre konsumentskydd än både svensk licens och MGA. Tvister hanteras enligt licensens egna regler — inte Allmänna reklamationsnämnden eller svensk konsumenträtt. Sajten är inte ansluten till Spelpaus.se. Vinster kan vara skattepliktiga eftersom Curaçao ligger utanför EES.",
          "Det är inte rätt val om du har spelproblem eller är avstängd via Spelpaus. För dig som söker en seriös, reglerad upplevelse är ett svenskt licensierat alternativ att föredra.",
        ],
        paragraphs_en: [
          "A Curaçao license means lower consumer protection than both the Swedish license and MGA. Disputes are handled under the license's own rules — not Sweden's ARN or Swedish consumer law. The site is not connected to Spelpaus.se. Winnings may be taxable because Curaçao sits outside the EEA.",
          "It is not the right choice if you struggle with gambling or are excluded via Spelpaus. If you want a regulated, serious experience, a Swedish-licensed alternative is preferable.",
        ],
      },
      {
        heading_sv: "",
        heading_en: "",
        paragraphs_sv: [
          "Spelutbudet är brett med titlar från Pragmatic Play, Hacksaw Gaming och Nolimit City.",
        ],
        paragraphs_en: [
          "The game library is broad with titles from Pragmatic Play, Hacksaw Gaming and Nolimit City.",
        ],
      },
    ],
    updated_at: "2026-05-01",
  },
  {
    id: "casino-instant-spel",
    name: "InstantSpel",
    slug: "instantspel",
    website_url: "https://www.instantspel.example/",
    logo_url: "/brands/instantspel.svg",
    operator_name: "Estonian operator",
    license_type: "estonia",
    license_authority: "Estonian Tax and Customs Board",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: true,
    zimpler_supported: false,
    brite_supported: true,
    crypto_supported: false,
    payment_methods: ["trustly", "brite", "visa"],
    game_providers: ["pragmatic-play", "evolution", "playngo"],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.6,
    ratings: { safety: 3.4, bonus: 3.8, payments: 4.2, games: 3.6, mobile: 3.6, support: 3.2 },
    pros_sv: ["Snabba uttag via Trustly", "Brite-stöd"],
    cons_sv: ["Ingen svensk licens", "Inte anslutet till Spelpaus"],
    pros_en: ["Fast Trustly withdrawals", "Brite supported"],
    cons_en: ["No Swedish license", "Not on Spelpaus"],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "InstantSpel är ett internationellt pay-and-play-casino med estnisk licens. Sajten profilerar sig på snabba uttag via Trustly och Brite men är inte ansluten till Spelpaus och saknar svensk konsumenträtt.",
    intro_en:
      "InstantSpel is an international pay-and-play casino with an Estonian license. The site is positioned on fast Trustly and Brite withdrawals but is not connected to Spelpaus and is not covered by Swedish consumer law.",
    body_sv:
      "Estnisk licens har begränsat skydd jämfört med svensk licens. Kontrollera villkor noggrant och var medveten om skattekonsekvenser.",
    body_en:
      "An Estonian license has limited protection compared with a Swedish license. Check the terms carefully and be aware of tax implications.",
    long_sections: [
      {
        heading_sv: "Estnisk licens — vad innebär det?",
        heading_en: "What does an Estonian license mean?",
        paragraphs_sv: [
          "Estland är medlem i EU och har ett eget licenssystem som administreras av Estonian Tax and Customs Board. Licensen kräver KYC, AML-kontroller och RNG-revision men inkluderar inte Spelpaus.se.",
        ],
        paragraphs_en: [
          "Estonia is an EU member with its own licensing system administered by the Estonian Tax and Customs Board. The license requires KYC, AML controls and RNG audits but does not include Spelpaus.se.",
        ],
      },
      {
        heading_sv: "Pay-and-play med Trustly",
        heading_en: "Pay-and-play with Trustly",
        paragraphs_sv: [
          "InstantSpel kör pay-and-play-flödet: ingen registrering, ingen lösenord, allt går via BankID och Trustly. Insättning, identifiering och senare uttag sker till samma bankkonto. Det är snabbt men förutsätter att du faktiskt har kontroll över din spelekonomi — det finns ingen Spelpaus att stänga av med.",
        ],
        paragraphs_en: [
          "InstantSpel runs the pay-and-play flow: no registration, no password, all goes through BankID and Trustly. Deposit, identification and later withdrawal use the same bank account. It is fast but assumes you actually have your gambling under control — there is no Spelpaus to fall back on.",
        ],
      },
      {
        heading_sv: "Vår sammanfattande bedömning",
        heading_en: "Our overall verdict",
        paragraphs_sv: [
          "InstantSpel är ett alternativ för spelare som specifikt vill spela utanför svensk licens och uppskattar pay-and-play. För majoriteten av svenska spelare är ett licensierat alternativ tryggare och har samma snabba Trustly-uttag — utan tappet i konsumentskydd.",
        ],
        paragraphs_en: [
          "InstantSpel is an option for players who specifically want to play outside the Swedish license and value pay-and-play. For the majority of Swedish players a licensed alternative is safer and ships the same fast Trustly withdrawals — without the loss in consumer protection.",
        ],
      },
    ],
    updated_at: "2026-05-01",
  },

  // ─── Additional Swedish-licensed brands ─────────────────────────────────
  {
    id: "casino-casinostugan",
    name: "Casinostugan",
    slug: "casinostugan",
    website_url: "https://www.casinostugan.com/",
    logo_url: "/brands/casinostugan.png",
    operator_name: "Hero Gaming Ltd",
    license_type: "swedish",
    license_authority: "Spelinspektionen",
    accepts_sweden: true,
    swedish_license: true,
    spelpaus_connected: true,
    bankid_supported: true,
    swish_supported: true,
    trustly_supported: true,
    zimpler_supported: true,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: ["swish", "trustly", "zimpler", "visa", "mastercard"],
    game_providers: ["netent", "playngo", "evolution", "pragmatic-play", "yggdrasil"],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 4.3,
    ratings: { safety: 4.7, bonus: 4.0, payments: 4.5, games: 4.3, mobile: 4.4, support: 4.2 },
    pros_sv: ["Tydligt svenskt fokus", "Bra mobillobby"],
    cons_sv: [],
    pros_en: ["Clear Sweden focus", "Good mobile lobby"],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    published: true,
    index_status: "index",
    intro_sv:
      "Casinostugan drivs av Hero Gaming och har funnits på den svenska marknaden i flera år. Sajten har en mysig stuga-tema och fokuserar tydligt på svenska spelare med Swish, Trustly och Zimpler.",
    intro_en:
      "Casinostugan is operated by Hero Gaming and has been on the Swedish market for several years. The site has a cosy cabin theme and focuses clearly on Swedish players with Swish, Trustly and Zimpler.",
    body_sv:
      "Spelutbudet täcker både slots, livecasino och bordsspel.",
    body_en:
      "The game library covers slots, live casino and table games.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-snabbare",
    name: "Snabbare",
    slug: "snabbare",
    website_url: "https://www.snabbare.com/",
    logo_url: "/brands/snabbare.png",
    operator_name: "Internationell operatör",
    license_type: "estonia",
    license_authority: "Estonian Tax and Customs Board",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: true,
    swish_supported: false,
    trustly_supported: true,
    zimpler_supported: false,
    brite_supported: true,
    crypto_supported: false,
    payment_methods: ["trustly", "brite"],
    game_providers: ["pragmatic-play", "playngo", "evolution"],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.7,
    ratings: { safety: 3.4, bonus: 4.0, payments: 4.3, games: 3.7, mobile: 3.7, support: 3.3 },
    pros_sv: ["Pay-and-play med BankID", "Snabba uttag"],
    cons_sv: ["Ingen svensk licens", "Inte ansluten till Spelpaus"],
    pros_en: ["Pay-and-play with BankID", "Fast withdrawals"],
    cons_en: ["No Swedish license", "Not connected to Spelpaus"],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Snabbare är ett pay-and-play-casino med fokus på snabba uttag via Trustly och Brite. Sajten har estnisk licens och är inte ansluten till Spelpaus.",
    intro_en:
      "Snabbare is a pay-and-play casino focused on fast Trustly and Brite withdrawals. It holds an Estonian license and is not connected to Spelpaus.",
    body_sv:
      "Som med alla casinon utan svensk licens — kontrollera villkor, var medveten om eventuell skatteplikt och var disciplinerad med insatsstorleken.",
    body_en:
      "As with all casinos without a Swedish license — check the terms, be aware of possible tax implications, and be disciplined with bet sizes.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-nordicbet",
    name: "NordicBet",
    slug: "nordicbet",
    website_url: "https://www.nordicbet.com/",
    logo_url: "/brands/nordicbet.png",
    operator_name: "Betsson AB",
    license_type: "swedish",
    license_authority: "Spelinspektionen",
    accepts_sweden: true,
    swedish_license: true,
    spelpaus_connected: true,
    bankid_supported: true,
    swish_supported: true,
    trustly_supported: true,
    zimpler_supported: true,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: ["swish", "trustly", "zimpler", "visa", "mastercard"],
    game_providers: ["netent", "playngo", "evolution", "pragmatic-play", "microgaming"],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 4.2,
    ratings: { safety: 4.7, bonus: 4.0, payments: 4.5, games: 4.2, mobile: 4.3, support: 4.1 },
    pros_sv: ["Nordiskt fokus", "Stark sportsbook"],
    cons_sv: ["Casinodelen är konservativ"],
    pros_en: ["Nordic focus", "Strong sportsbook"],
    cons_en: ["Casino side is conservative"],
    priority_brand: false,
    verification_status: "needs_review",
    published: true,
    index_status: "index",
    intro_sv:
      "NordicBet ingår i Betsson-koncernen och är en av Nordens äldsta speloperatörer. Sajten har svensk licens och kombinerar casino med en stark sportsbook.",
    intro_en:
      "NordicBet is part of the Betsson group and one of the Nordics' oldest gambling operators. The site holds a Swedish license and combines casino with a strong sportsbook.",
    body_sv:
      "Eftersom NordicBet och Betsson delar koncern är många funktioner och betalningsalternativ identiska. Casino, betting och poker finns under samma konto.",
    body_en:
      "Because NordicBet and Betsson share a parent, many features and payments are identical. Casino, betting and poker are under one account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-spelahar",
    name: "Spelahär",
    slug: "spelahar",
    website_url: "https://www.spelahar.com/",
    logo_url: "/brands/spelahar.svg",
    operator_name: "Internationell operatör",
    license_type: "curacao",
    license_authority: "Curaçao Gaming Control Board",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: true,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: true,
    payment_methods: ["trustly", "bitcoin", "ethereum", "visa"],
    game_providers: ["pragmatic-play", "hacksaw-gaming", "nolimit-city"],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.4,
    ratings: { safety: 3.0, bonus: 4.2, payments: 3.8, games: 3.6, mobile: 3.5, support: 3.0 },
    pros_sv: ["Krypto-betalningar"],
    cons_sv: ["Curaçao-licens har lägre konsumentskydd", "Inte ansluten till Spelpaus"],
    pros_en: ["Crypto payments"],
    cons_en: ["Curaçao license has lower protection", "Not on Spelpaus"],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Spelahär är ett internationellt casino med Curaçao-licens som riktar sig till svenska spelare.",
    intro_en:
      "Spelahär is an international Curaçao-licensed casino targeting Swedish players.",
    body_sv:
      "Curaçao-licens innebär lägre konsumentskydd än svensk licens och MGA. Sajten är inte ansluten till Spelpaus.",
    body_en:
      "A Curaçao license means lower consumer protection than a Swedish license or MGA. The site is not connected to Spelpaus.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-hajper",
    name: "Hajper",
    slug: "hajper",
    website_url: "https://www.hajper.com/",
    logo_url: "/brands/hajper.png",
    operator_name: "Hajper Casino Ltd",
    license_type: "swedish",
    license_authority: "Spelinspektionen",
    accepts_sweden: true,
    swedish_license: true,
    spelpaus_connected: true,
    bankid_supported: true,
    swish_supported: true,
    trustly_supported: true,
    zimpler_supported: true,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: ["swish", "trustly", "zimpler"],
    game_providers: ["netent", "playngo", "pragmatic-play", "evolution", "hacksaw-gaming"],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 4.1,
    ratings: { safety: 4.6, bonus: 4.0, payments: 4.4, games: 4.1, mobile: 4.3, support: 4.0 },
    pros_sv: ["Snabb mobilupplevelse", "Tydligt svenskt gränssnitt"],
    cons_sv: ["Mindre etablerat varumärke"],
    pros_en: ["Fast mobile experience", "Clear Swedish UI"],
    cons_en: ["Smaller, newer brand"],
    priority_brand: false,
    verification_status: "needs_review",
    published: true,
    index_status: "index",
    intro_sv:
      "Hajper är ett relativt nytt svensk-licensierat casino med fokus på snabb mobilupplevelse och svenska betalningsmetoder. Lobbyn är ren och navigationen anpassad för enhandsläge.",
    intro_en:
      "Hajper is a relatively new Swedish-licensed casino with a focus on fast mobile and Swedish payment methods. The lobby is clean and navigation is built for one-handed use.",
    body_sv:
      "Som svenskt licensierat casino är Hajper anslutet till Spelpaus och kräver BankID.",
    body_en:
      "As a Swedish-licensed casino Hajper is connected to Spelpaus and requires BankID.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-gogo-casino",
    name: "Gogo Casino",
    slug: "gogo-casino",
    logo_url: "/brands/gogo-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-quick-casino",
    name: "Quick Casino",
    slug: "quick-casino",
    logo_url: "/brands/quick-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-campobet",
    name: "Campobet",
    slug: "campobet",
    logo_url: "/brands/campobet.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-maria-casino",
    name: "Maria Casino",
    slug: "maria-casino",
    logo_url: "/brands/maria-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-happy-casino",
    name: "Happy Casino",
    slug: "happy-casino",
    logo_url: "/brands/happy-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-comeon",
    name: "Comeon",
    slug: "comeon",
    logo_url: "/brands/comeon.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-lyllo-casino",
    name: "Lyllo Casino",
    slug: "lyllo-casino",
    logo_url: "/brands/lyllo-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-lucky-casino",
    name: "Lucky Casino",
    slug: "lucky-casino",
    logo_url: "/brands/lucky-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-videoslots",
    name: "Videoslots",
    slug: "videoslots",
    logo_url: "/brands/videoslots.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-betmgm",
    name: "Betmgm",
    slug: "betmgm",
    logo_url: "/brands/betmgm.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-golden-bull",
    name: "Golden Bull",
    slug: "golden-bull",
    logo_url: "/brands/golden-bull.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-bethard",
    name: "Bethard",
    slug: "bethard",
    logo_url: "/brands/bethard.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-mega-riches",
    name: "Mega Riches",
    slug: "mega-riches",
    logo_url: "/brands/mega-riches.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-interwetten",
    name: "Interwetten",
    slug: "interwetten",
    logo_url: "/brands/interwetten.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-yoyo-casino",
    name: "Yoyo Casino",
    slug: "yoyo-casino",
    logo_url: "/brands/yoyo-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-frank-casino",
    name: "Frank Casino",
    slug: "frank-casino",
    logo_url: "/brands/frank-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-speedy-casino",
    name: "Speedy Casino",
    slug: "speedy-casino",
    logo_url: "/brands/speedy-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-frank-fred",
    name: "Frank Fred",
    slug: "frank-fred",
    logo_url: "/brands/frank-fred.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-chanz",
    name: "Chanz",
    slug: "chanz",
    logo_url: "/brands/chanz.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-fastbet",
    name: "Fastbet",
    slug: "fastbet",
    logo_url: "/brands/fastbet.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-luna-casino",
    name: "Luna Casino",
    slug: "luna-casino",
    logo_url: "/brands/luna-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-swift-casino",
    name: "Swift Casino",
    slug: "swift-casino",
    logo_url: "/brands/swift-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-pop-casino",
    name: "Pop Casino",
    slug: "pop-casino",
    logo_url: "/brands/pop-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-voodoodreams",
    name: "Voodoodreams",
    slug: "voodoodreams",
    logo_url: "/brands/voodoodreams.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-pronto-casino",
    name: "Pronto Casino",
    slug: "pronto-casino",
    logo_url: "/brands/pronto-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-casumo",
    name: "Casumo",
    slug: "casumo",
    logo_url: "/brands/casumo.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-kungaslottet",
    name: "Kungaslottet",
    slug: "kungaslottet",
    logo_url: "/brands/kungaslottet.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-expekt",
    name: "Expekt",
    slug: "expekt",
    logo_url: "/brands/expekt.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-storspelare",
    name: "Storspelare",
    slug: "storspelare",
    logo_url: "/brands/storspelare.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-flax-casino",
    name: "Flax Casino",
    slug: "flax-casino",
    logo_url: "/brands/flax-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-otto-casino",
    name: "Otto Casino",
    slug: "otto-casino",
    logo_url: "/brands/otto-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-spelklubben",
    name: "Spelklubben",
    slug: "spelklubben",
    logo_url: "/brands/spelklubben.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-ninja-casino",
    name: "Ninja Casino",
    slug: "ninja-casino",
    logo_url: "/brands/ninja-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-speedybet",
    name: "Speedybet",
    slug: "speedybet",
    logo_url: "/brands/speedybet.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-lucky-days",
    name: "Lucky Days",
    slug: "lucky-days",
    logo_url: "/brands/lucky-days.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-cherry-casino",
    name: "Cherry Casino",
    slug: "cherry-casino",
    logo_url: "/brands/cherry-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-swiper-casino",
    name: "Swiper Casino",
    slug: "swiper-casino",
    logo_url: "/brands/swiper-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-vbet",
    name: "Vbet",
    slug: "vbet",
    logo_url: "/brands/vbet.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-coolbet",
    name: "Coolbet",
    slug: "coolbet",
    logo_url: "/brands/coolbet.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-jalla-casino",
    name: "Jalla Casino",
    slug: "jalla-casino",
    logo_url: "/brands/jalla-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-jubla-casino",
    name: "Jubla Casino",
    slug: "jubla-casino",
    logo_url: "/brands/jubla-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-casinocasino",
    name: "Casinocasino",
    slug: "casinocasino",
    logo_url: "/brands/casinocasino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-betsafe",
    name: "Betsafe",
    slug: "betsafe",
    logo_url: "/brands/betsafe.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-pronto-live",
    name: "Pronto Live",
    slug: "pronto-live",
    logo_url: "/brands/pronto-live.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-lodur",
    name: "Lodur",
    slug: "lodur",
    logo_url: "/brands/lodur.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-dbet",
    name: "Dbet",
    slug: "dbet",
    logo_url: "/brands/dbet.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-svenska-spel-casino",
    name: "Svenska Spel Casino",
    slug: "svenska-spel-casino",
    logo_url: "/brands/svenska-spel-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-geckoplay",
    name: "Geckoplay",
    slug: "geckoplay",
    logo_url: "/brands/geckoplay.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-klirr-casino",
    name: "Klirr Casino",
    slug: "klirr-casino",
    logo_url: "/brands/klirr-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-casino-epic",
    name: "Casino Epic",
    slug: "casino-epic",
    logo_url: "/brands/casino-epic.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-mr-green",
    name: "Mr Green",
    slug: "mr-green",
    logo_url: "/brands/mr-green.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-vera-john",
    name: "Vera John",
    slug: "vera-john",
    logo_url: "/brands/vera-john.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-bet365",
    name: "Bet365",
    slug: "bet365",
    logo_url: "/brands/bet365.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-paf",
    name: "Paf",
    slug: "paf",
    logo_url: "/brands/paf.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-playtoro",
    name: "Playtoro",
    slug: "playtoro",
    logo_url: "/brands/playtoro.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-duelz-casino",
    name: "Duelz Casino",
    slug: "duelz-casino",
    logo_url: "/brands/duelz-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-royale500",
    name: "Royale500",
    slug: "royale500",
    logo_url: "/brands/royale500.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-turbovegas",
    name: "Turbovegas",
    slug: "turbovegas",
    logo_url: "/brands/turbovegas.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-turbonino",
    name: "Turbonino",
    slug: "turbonino",
    logo_url: "/brands/turbonino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
  {
    id: "casino-fun-casino",
    name: "Fun Casino",
    slug: "fun-casino",
    logo_url: "/brands/fun-casino.png",
    license_type: "unknown",
    accepts_sweden: true,
    swedish_license: false,
    spelpaus_connected: false,
    bankid_supported: false,
    swish_supported: false,
    trustly_supported: false,
    zimpler_supported: false,
    brite_supported: false,
    crypto_supported: false,
    payment_methods: [],
    game_providers: [],
    support_languages: ["sv", "en"],
    mobile_supported: true,
    overall_rating: 3.5,
    ratings: { safety: 3.5, bonus: 3.5, payments: 3.5, games: 3.5, mobile: 3.5, support: 3.5 },
    pros_sv: [],
    cons_sv: [],
    pros_en: [],
    cons_en: [],
    priority_brand: false,
    verification_status: "needs_review",
    source_reference: "competitor_fallback",
    published: true,
    index_status: "index",
    intro_sv:
      "Läs licens, betalningsvillkor och spelregler innan du registrerar konto.",
    intro_en:
      "Read the licence, payment terms and game rules before registering an account.",
    updated_at: "2026-05-01",
  },
];

export function findCasino(slug: string): Casino | undefined {
  return casinos.find((c) => c.slug === slug);
}

export function listCasinos(filter?: {
  swedishLicense?: boolean;
  noSwedishLicense?: boolean;
  noSpelpaus?: boolean;
  swish?: boolean;
  bankid?: boolean;
  trustly?: boolean;
  zimpler?: boolean;
  brite?: boolean;
  crypto?: boolean;
  priorityBrand?: boolean;
  paymentMethod?: string;
  provider?: string;
  searchQuery?: string;
}): Casino[] {
  let result = casinos.filter((c) => c.published);
  if (!filter) return result;
  if (filter.swedishLicense) result = result.filter((c) => c.swedish_license);
  if (filter.noSwedishLicense) result = result.filter((c) => !c.swedish_license);
  if (filter.noSpelpaus) result = result.filter((c) => !c.spelpaus_connected);
  if (filter.swish) result = result.filter((c) => c.swish_supported);
  if (filter.bankid) result = result.filter((c) => c.bankid_supported);
  if (filter.trustly) result = result.filter((c) => c.trustly_supported);
  if (filter.zimpler) result = result.filter((c) => c.zimpler_supported);
  if (filter.brite) result = result.filter((c) => c.brite_supported);
  if (filter.crypto) result = result.filter((c) => c.crypto_supported);
  if (filter.priorityBrand) result = result.filter((c) => c.priority_brand);
  if (filter.paymentMethod)
    result = result.filter((c) => c.payment_methods.includes(filter.paymentMethod!));
  if (filter.provider)
    result = result.filter((c) => c.game_providers.includes(filter.provider!));
  if (filter.searchQuery) {
    const q = filter.searchQuery.toLowerCase();
    result = result.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.slug.toLowerCase().includes(q) ||
        (c.intro_sv ?? "").toLowerCase().includes(q) ||
        (c.intro_en ?? "").toLowerCase().includes(q),
    );
  }
  return result;
}
